import React, { useState, useEffect } from "react";
import { apiService } from "../../utils/api";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, TextField, Box, Typography, FormControl, InputLabel, Button, TablePagination, Card, Grid, CardContent } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchIcon from "@mui/icons-material/Search";

const SubscriptionManagement = () => {
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [status, setStatus] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (status !== "all") params.append("status", status);
      if (paymentMethod !== "all") params.append("paymentMethod", paymentMethod);
      if (startDate) params.append("startDate", startDate.toISOString());
      if (endDate) params.append("endDate", endDate.toISOString());

      const response = await apiService.getAdminSubscriptioList(params);
      setAllSubscriptions(response.data.data);
      // Apply initial pagination
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setSubscriptions(response.data.data.slice(startIndex, endIndex));
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const startIndex = newPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    setSubscriptions(allSubscriptions.slice(startIndex, endIndex));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    const startIndex = 0;
    const endIndex = newRowsPerPage;
    setSubscriptions(allSubscriptions.slice(startIndex, endIndex));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Subscription Management
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card >
            <CardContent>
              <Typography variant="h4" align="center">
                {allSubscriptions.length}
              </Typography>
              <Typography variant="subtitle1" align="center">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "success.light" }}>
            <CardContent>
              <Typography variant="h4" align="center">
                {allSubscriptions.filter((subscription) => subscription.paymentMethod === "Razorpay").length}
              </Typography>
              <Typography variant="subtitle1" align="center">
                Razorpay
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "warning.light" }}>
            <CardContent>
              <Typography variant="h4" align="center">
                {allSubscriptions.filter((subscription) => subscription.paymentMethod === "manual").length}
              </Typography>
              <Typography variant="subtitle1" align="center">
                Manual
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <br /><br />

      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Payment Method</InputLabel>
          <Select value={paymentMethod} label="Payment Method" onChange={(e) => setPaymentMethod(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Razorpay">Razorpay</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker label="Start Date" value={startDate} onChange={(newValue) => setStartDate(newValue)} renderInput={(params) => <TextField {...params} />} />
          <DatePicker label="End Date" value={endDate} onChange={(newValue) => setEndDate(newValue)} renderInput={(params) => <TextField {...params} />} />
        </LocalizationProvider>

        <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={fetchSubscriptions} disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User ID</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Method</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.map((subscription) => (
              <TableRow key={subscription._id}>
                <TableCell>
                  {subscription.student.username} ({subscription.student.rollNo}){" "}
                </TableCell>
                <TableCell>{subscription.planType.toUpperCase()}</TableCell>
                <TableCell>{subscription.status}</TableCell>
                <TableCell>{new Date(subscription.startDate).toLocaleDateString()}</TableCell>
                <TableCell>{new Date(subscription.endDate).toLocaleDateString()}</TableCell>
                <TableCell>₹{subscription.amount}</TableCell>
                <TableCell>{subscription.paymentMethod}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={allSubscriptions.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </TableContainer>
    </Box>
  );
};

export default SubscriptionManagement;
